import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { RouteConfig } from 'aurelia-router';

import { CustomisationConfig } from '~/components/customisation/customisation-config';
import { CommonURLParam } from '~/constants';
import { getAreaNames } from '~/utils/customisation-config-utils';
import { FetchUtils, HttpMethod } from '~/utils/fetch-utils';

@autoinject
export class CustomisationsApi {
	private lastFormName: string = '';

	private lastCustomisationName: string = '';

	/**
	 * The last customisation fetched via "getCustomisation"
	 */
	private lastCustomisation: CustomisationConfig | undefined;

	constructor(protected eventAggregator: EventAggregator) {
		this.eventAggregator.subscribe(
			'router:navigation:processing',
			this.onRouterNavigationProcessing,
		);
	}

	public static getFormNameOnActivate(
		routeConfig: RouteConfig,
		formNameEnding: string = '',
	): string {
		let formName = routeConfig.name;
		if (formNameEnding) {
			formName = formName + formNameEnding;
		}
		return formName;
	}

	public static getCustomisationNameOnActivate(params): string {
		return params[CommonURLParam.CUSTOMISATION]
			? params[CommonURLParam.CUSTOMISATION]
			: '';
	}

	public getCustomisationOnActivate(
		params,
		routeConfig: RouteConfig,
		formNameEnding: string = '',
	): Promise<CustomisationConfig> {
		const formName = CustomisationsApi.getFormNameOnActivate(
			routeConfig,
			formNameEnding,
		);
		const customisationName =
			CustomisationsApi.getCustomisationNameOnActivate(params);

		return this.getCustomisation(formName, customisationName);
	}

	public async getCustomisation(
		formName: string,
		customisationName?: string,
	): Promise<CustomisationConfig> {
		if (!formName) {
			throw new Error(
				'getCustomisation: Cannot use empty formName string',
			);
		}
		const data = await FetchUtils.getJSON<CustomisationConfig>(
			'api/customisations/:formName/:customisationName',
			{
				formName,
				customisationName,
			},
		);
		this.lastFormName = formName;
		this.lastCustomisationName = customisationName;
		this.lastCustomisation = data;
		return data;
	}

	public async putCustomisation(
		formName: string,
		customisationName: '' | string,
		customisationConfig: CustomisationConfig,
	): Promise<void> {
		if (!formName) {
			throw new Error(
				'putCustomisation: Cannot use empty "formName" string',
			);
		}
		await FetchUtils.fetchJSON<CustomisationConfig>(
			HttpMethod.PUT,
			'api/customisations/:formName/:customisationName',
			{
				formName,
				customisationName,
			},
			customisationConfig,
		);
		return;
	}

	public async putCustomisationWithValue(
		formName: string,
		customisationName: '' | string,
		customisationConfig: CustomisationConfig,
	): Promise<CustomisationConfig> {
		if (!formName) {
			throw new Error(
				'putCustomisation: Cannot use empty "formName" string',
			);
		}
		const data = await FetchUtils.fetchJSON<CustomisationConfig>(
			HttpMethod.PUT,
			'api/customisations/:formName/:customisationName',
			{
				formName,
				customisationName,
			},
			customisationConfig,
		);
		this.lastFormName = formName;
		this.lastCustomisationName = customisationName;
		this.lastCustomisation = data;
		return data;
	}

	public getLastCustomisationFormName(): string {
		return this.lastFormName;
	}

	public getLastCustomisationCustomisationName(): string {
		return this.lastCustomisationName;
	}

	/**
	 * Get all the available customisation areas from the last "getCustomisation" call.
	 */
	public getLastCustomisationAreaNames(): string[] {
		if (!this.lastCustomisation) {
			return [];
		}
		return getAreaNames(this.lastCustomisation.template);
	}

	private onRouterNavigationProcessing = (): void => {
		// NOTE: Jake: 2019-06-07 - WEBUI-2092
		// Reset current loaded customisation so that you cannot select from a list
		// of customisations from the previous page/form visited.
		this.lastCustomisationName = '';
		this.lastCustomisation = undefined;
	};
}
